import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { getCachedClients, cacheClients } from '@/lib/offlineDataCache';
import type { Client } from '@/types/database';
import { Search, User, Phone, MapPin, Loader2, WifiOff } from 'lucide-react';

interface ClientSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (client: Client) => void;
}

export function ClientSelector({ open, onOpenChange, onSelect }: ClientSelectorProps) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline } = useNetworkStatus();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && user) {
      loadClients();
    }
  }, [open, user, isOnline, activeCompany?.id]);

  const loadClients = async () => {
    if (!user) return;
    
    setLoading(true);
    
    // Always try cache first for instant display
    const cached = getCachedClients(user.id);
    if (cached && cached.length > 0) {
      setClients(cached as Client[]);
      setLoading(false);
    }
    
    // Only fetch from server if online
    if (isOnline && activeCompany) {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('name');

        if (data && !error) {
          setClients(data as Client[]);
          cacheClients(user.id, data);
        }
      } catch (e) {
        console.error('[ClientSelector] Error fetching clients:', e);
        // Keep cached data on error
      }
    }
    
    setLoading(false);
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (client: Client) => {
    onSelect(client);
    onOpenChange(false);
    setSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Selecionar cliente
            {!isOnline && (
              <span className="flex items-center gap-1 text-xs text-amber-600 font-normal">
                <WifiOff className="h-3 w-3" />
                Offline
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 max-h-[50vh] pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <Card
                key={client.id}
                className="cursor-pointer transition-colors hover:bg-accent/50"
                onClick={() => handleSelect(client)}
              >
                <CardContent className="p-3">
                  <p className="font-medium">{client.name}</p>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {client.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {client.phone}
                      </span>
                    )}
                    {client.address && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {client.address}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <User className="mb-2 h-10 w-10 opacity-50" />
            <p>{search ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}</p>
          </div>
          )}
        </div>

        <Button variant="outline" onClick={() => onOpenChange(false)} className="mt-2">
          Cancelar
        </Button>
      </DialogContent>
    </Dialog>
  );
}
